<div class="overflow-hidden rounded-lg border border-greyscale-200 bg-white">
  <button
    class="flex w-full items-center justify-between p-4 transition-colors hover:bg-primary-50/30"
    (click)="toggle()"
  >
    <div class="flex items-center gap-3">
      <div
        class="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100"
      >
        <lucide-angular
          [img]="getIcon()"
          class="h-4 w-4 text-primary-600"
        ></lucide-angular>
      </div>
      <h3 class="text-lg font-semibold text-greyscale-900">{{ title }}</h3>
    </div>
    <!-- Total kept in the header: the tips card below stays in view when the
         section is folded, and still needs the figure it reduces. -->
    @if (hasData() && !showPercentage) {
      <span
        class="ml-auto mr-3 hidden text-paragraph-s-reg font-medium tabular-nums text-greyscale-800 sm:inline"
      >
        {{ getCenterValue() }}
        <span class="text-paragraph-xs-reg text-greyscale-500">{{
          getCenterLabel()
        }}</span>
      </span>
    }
    <div
      class="text-greyscale-600 transition-transform duration-75"
      [class.rotate-90]="isOpen"
    >
      <lucide-angular [img]="chevronRightIcon" class="h-5 w-5"></lucide-angular>
    </div>
  </button>

  @if (isOpen) {
    <div
      class="border-t border-greyscale-200 bg-primary-50 p-6 transition-all duration-75"
    >
      @if (hasData()) {
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <!-- Graphique responsive -->
          <div
            class="mx-auto flex items-center justify-center overflow-visible"
          >
            <app-chart-doughnut
              [data]="getSortedChartData()"
              [size]="chartSize.getSize('collapsibleSection')"
              [thickness]="chartSize.getThickness('collapsibleSection')"
              [centerValue]="getCenterValue()"
              [centerLabel]="getCenterLabel()"
              [showLabelInTooltip]="true"
            >
            </app-chart-doughnut>
          </div>

          <!-- Légende enrichie -->
          <div>
            <h4
              class="mb-4 border-b border-greyscale-200 pb-2 font-semibold text-greyscale-900"
            >
              {{ "impact.overview.detailedLegend" | transloco }}
            </h4>

            <div class="max-h-80 space-y-4 overflow-y-auto">
              @if (hasTimData) {
                <div class="text-paragraph-s-reg text-greyscale-500">
                  {{ "timWarning.short" | transloco }}
                  <app-tooltip-icon
                    position="top"
                    [message]="'timWarning.report' | transloco"
                  />
                </div>
              }
              @for (item of getEnrichedLegendData(); track $index) {
                <div
                  class="hover:bg-greyscale-25 flex cursor-pointer flex-col rounded py-2 transition-colors sm:flex-row sm:items-center sm:justify-between"
                  (mouseenter)="onLegendItemHover(item, $index)"
                  (mouseleave)="onLegendItemLeave()"
                >
                  <!-- Élément avec couleur et nom - mobile: pleine largeur -->
                  <div
                    class="flex min-w-0 items-center gap-2"
                    [pTooltip]="
                      item.isEcoFriendly
                        ? item.title
                          ? item.title +
                            ' (' +
                            item.label +
                            ') ' +
                            ('fields.isEcoFriendly' | transloco)
                          : item.label + ('fields.isEcoFriendly' | transloco)
                        : item.title
                          ? item.title + ' (' + item.label + ')'
                          : item.label
                    "
                    tooltipPosition="top"
                  >
                    <div
                      class="h-3 w-3 flex-shrink-0 rounded-full"
                      [style.background-color]="item.color"
                    ></div>
                    <div class="min-w-0">
                      @if (item.title) {
                        <span
                          class="block truncate text-paragraph-s-reg text-greyscale-700"
                          >{{ item.title }}
                          <span
                            class="ml-1 text-paragraph-xs-reg text-greyscale-500"
                            >({{ item.label }})</span
                          ></span
                        >
                      } @else {
                        <span
                          class="block truncate text-paragraph-s-reg text-greyscale-700"
                          >{{ item.label }}</span
                        >
                      }
                    </div>
                    @if (item.isEcoFriendly) {
                      <lucide-icon
                        name="leaf"
                        class="rounded-full bg-primary-100 p-0.5 text-primary-600"
                        [size]="16"
                      />
                    }
                  </div>

                  <!-- Valeurs - mobile: en dessous à droite, desktop: à droite -->
                  <div
                    class="mt-1 flex flex-shrink-0 items-center justify-end gap-3 pl-6 text-sm sm:mt-0 sm:pl-0"
                  >
                    <div class="text-right">
                      <span class="font-medium text-greyscale-800">{{
                        formatImpactValue(item.impact)
                      }}</span>
                      <span class="text-xs text-greyscale-500">
                        {{ getUnitForImpact(item.impact) }}</span
                      >
                    </div>
                    <div class="w-10 text-right">
                      <span class="text-xs font-medium text-primary-600"
                        >{{ item.percentage }}%</span
                      >
                    </div>
                  </div>
                </div>
              } @empty {
                <div class="py-8 text-center text-greyscale-500">
                  <span class="text-sm italic">{{
                    "impact.overview.noElements" | transloco
                  }}</span>
                </div>
              }
            </div>
          </div>
        </div>
      } @else {
        @if (categoryType !== "daily") {
          <div class="py-8 text-center">
            <div class="mb-2 text-greyscale-400">
              <lucide-angular
                [img]="getIcon()"
                class="mx-auto h-8 w-8"
              ></lucide-angular>
            </div>
            <p class="text-sm text-greyscale-600">
              {{
                "impact.stats.noDataFor"
                  | transloco: { title: (title || "").toLowerCase() }
              }}
            </p>
          </div>
        }
      }

      <!-- Content projeté pour les cas spéciaux comme le graphique par jour -->
      <ng-content></ng-content>
    </div>
  }
</div>
